import { database } from './firebase';
import { ref, get, onValue } from 'firebase/database';
import { DB_PATHS } from './schema';

/**
 * Normalizes URL and extracts hostname/domain
 */
export const extractDomain = (url) => {
  if (!url) return '';
  try {
    const fullUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    const parsed = new URL(fullUrl);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
};

/**
 * Returns a high-res favicon URL from Google S2 Favicon service
 */
export const getDomainFavicon = (url) => {
  const domain = extractDomain(url);
  if (!domain) return '';
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;
};

/**
 * Parses raw Firebase snapshot into sanitized array of active tested sites
 */
const transformSnapshot = (val) => {
  if (!val || typeof val !== 'object') return [];

  return Object.entries(val)
    .map(([id, item]) => ({
      id,
      ...item,
      domain: item.domain || extractDomain(item.url),
      productId: item.productId || '',
      productName: item.productName || item.scriptName || '',
      productCategory: item.productCategory || '',
      status: item.status || 'working',
      isActive: item.isActive !== false,
      accuracyRate: item.accuracyRate || '99.5%',
      category: item.category || 'Crypto Platform',
      iconUrl: item.iconUrl || getDomainFavicon(item.url)
    }))
    .filter((site) => site.isActive)
    .sort((a, b) => {
      const statusOrder = { working: 0, testing: 1, maintenance: 2, unsupported: 3 };
      const statusDiff = (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9);
      if (statusDiff !== 0) return statusDiff;
      return new Date(b.lastTested || b.updatedAt || 0) - new Date(a.lastTested || a.updatedAt || 0);
    });
};

/**
 * Fetches all publicly active tested sites once
 */
export const fetchTestedSites = async () => {
  try {
    const snapshot = await get(ref(database, DB_PATHS.TESTED_SITES));
    if (!snapshot.exists()) return [];
    return transformSnapshot(snapshot.val());
  } catch (error) {
    console.error('Error fetching tested sites:', error);
    return [];
  }
};

/**
 * Subscribes to real-time updates for tested sites
 */
export const subscribeTestedSites = (callback) => {
  const sitesRef = ref(database, DB_PATHS.TESTED_SITES);
  return onValue(
    sitesRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback(transformSnapshot(snapshot.val()));
      } else {
        callback([]);
      }
    },
    (error) => {
      console.error('Realtime subscription error for tested sites:', error);
      callback([]);
    }
  );
};

/**
 * Retrieves tested sites specifically for a given product:
 * - If standard product: returns sites directly tied to this productId.
 * - If bundled product: returns all tested sites from all individual products included in the bundle.
 */
export const getTestedSitesForProduct = (sites = [], product = null, lookupIndex = {}) => {
  if (!product) return [];

  const isBundle = String(product.category || '').toLowerCase() === 'bundle';

  if (isBundle) {
    const includedIds = (
      Array.isArray(product.Products) ? product.Products : product.bundleItems || []
    )
      .map(String)
      .filter(Boolean);

    // 1. First check testedSites stored directly on the included products
    const directIncludedSites = [];
    includedIds.forEach((pid) => {
      const bp = lookupIndex[pid];
      if (bp && Array.isArray(bp.testedSites)) {
        bp.testedSites.forEach((site) => {
          directIncludedSites.push({
            ...site,
            productId: pid,
            productName: bp.name || bp.Name || 'Included Script',
            isInheritedFromBundle: true
          });
        });
      }
    });

    if (directIncludedSites.length > 0) {
      return directIncludedSites;
    }

    // 2. Fallback to global sites collection matching included IDs
    return (Array.isArray(sites) ? sites : [])
      .filter((site) => includedIds.includes(String(site.productId)))
      .map((site) => {
        const matchedProduct = lookupIndex[String(site.productId)];
        return {
          ...site,
          isInheritedFromBundle: true,
          productName: site.productName || matchedProduct?.name || matchedProduct?.Name || 'Included Script'
        };
      });
  }

  // Single individual product:
  // 1. Directly check product.testedSites stored on product
  if (Array.isArray(product.testedSites) && product.testedSites.length > 0) {
    return product.testedSites.map((site) => ({
      ...site,
      productId: product.id,
      productName: product.name || product.Name
    }));
  }

  // 2. Fallback to global sites collection matching productId
  return (Array.isArray(sites) ? sites : []).filter((site) => {
    if (site.productId && String(site.productId) === String(product.id)) {
      return true;
    }
    if (!site.productId && site.scriptName && product.name) {
      return site.scriptName.trim().toLowerCase() === product.name.trim().toLowerCase();
    }
    return false;
  });
};
